import type { GridRowId, GridRowParams } from '@mui/x-data-grid';
import { extract, isNotNullOrUndefined } from '@weft/utils';
import { useContractProductsOverviews } from 'apps/app/src/hooks/requests/use-contract-products-overviews';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { VyTable } from '../../../../../../../components/new-design/table/vy-table/VyTable';
import { CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS } from '../net-suite-export-contract-dialog-consts/contract-products-overviews-columns';

type ContractProductsOverviewsTableProps = {
  contractId: string;
  onProductSelect: (selectedProductsIds: string[]) => void;
};

export const ContractProductsOverviewsContent: React.FC<ContractProductsOverviewsTableProps> = ({ contractId, onProductSelect }) => {
  const { data: { productsOverviews }, isFetching } = useContractProductsOverviews(contractId);

  const initialSelectedProductIds = useMemo(() => {
    const productsWithErpId = productsOverviews.filter(product => isNotNullOrUndefined(product.erpMetadata));

    return extract(productsWithErpId, 'id');
  }, [productsOverviews]);

  const [selectedProductsIds, setSelectedProductsIds] = useState<string[]>(initialSelectedProductIds);

  const handleSelectionChange = useCallback((selectedRowKeys: GridRowId[]) => {
    const currentProductsIds = selectedRowKeys.map((key) => key.toString());
    setSelectedProductsIds(currentProductsIds);
  }, [setSelectedProductsIds]);

  const rowSelectionModel = useMemo(() => isFetching ? [] : selectedProductsIds ?? [], [isFetching, selectedProductsIds]);

  useEffect(() => {
    onProductSelect(selectedProductsIds);
  }, [selectedProductsIds, onProductSelect]);

  return isNotNullOrUndefined(productsOverviews) ?
    (
      <VyTable
        columns={CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS}
        checkboxSelection={true}
        disableRowSelectionOnClick
        onRowSelectionModelChange={(newRowSelectionModel) => handleSelectionChange(newRowSelectionModel)}
        items={productsOverviews}
        itemsCount={productsOverviews.length}
        rowSelectionModel={rowSelectionModel}
        getRowId={(row) => row.id}
        showPagination={false}
        dontUseGlobalState
        isRowSelectable={(params: GridRowParams) => isNotNullOrUndefined(params.row.erpMetadata)}
        slotProps={{
          baseCheckbox: {
            onClick: (event) => event.stopPropagation(),
          },
        }}
        sx={{
          marginBottom: '15px',
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'white',
          },
          '& .MuiDataGrid-columnSeparator.MuiDataGrid-columnSeparator--sideRight': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
            display: 'none',
          },
        }}
      />
    ) : null;
};
